// SPDX-License-Identifier: AGPL-3.0-or-later

import type {FormInputs} from '@app/features/channel/components/modals/channel_tabs/channel_overview_tab/shared';
import type {ChannelLayout, ChannelTitleAnimation} from '@app/features/channel/utils/ChannelAppearanceUtils';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {CompactComboboxRow} from '@app/features/user/components/modals/tabs/components/CompactComboboxRow';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {Controller, type UseFormReturn} from 'react-hook-form';

const TITLE_COLOR_DESCRIPTOR = msg({message: 'Channel title color'});
const TITLE_COLOR_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Choose the color shown for this channel in the channel list.',
});
const TITLE_ANIMATION_DESCRIPTOR = msg({message: 'Channel title animation'});
const TITLE_ANIMATION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Choose a visual effect for the channel title. Reduced-motion preferences are always respected.',
});

const LAYOUT_LABELS: Record<ChannelLayout, string> = {
	default: 'Chat padrão',
	feed: 'Feed',
	marketplace: 'Marketplace',
	music: 'Música',
	raffle: 'Sorteios',
};

const ANIMATION_LABELS: Record<ChannelTitleAnimation, ReturnType<typeof msg>> = {
	none: msg({message: 'None'}),
	glow: msg({message: 'Golden glow'}),
	pulse: msg({message: 'Soft pulse'}),
	shimmer: msg({message: 'Shimmer'}),
	float: msg({message: 'Gentle float'}),
};

export const ChannelAppearanceSection: React.FC<{form: UseFormReturn<FormInputs>; channelType: number}> = ({
	form,
	channelType,
}) => {
	const {i18n} = useLingui();
	const animationOptions = (Object.keys(ANIMATION_LABELS) as Array<ChannelTitleAnimation>).map((value) => ({
		value,
		label: i18n._(ANIMATION_LABELS[value]),
	}));
	const layouts: Array<ChannelLayout> =
		channelType === ChannelTypes.GUILD_VOICE ? ['default', 'music'] : ['default', 'feed', 'marketplace', 'raffle'];
	const layoutOptions = layouts.map((value) => ({value, label: LAYOUT_LABELS[value]}));

	return (
		<>
			<Controller
				name="title_layout"
				control={form.control}
				render={({field}) => (
					<CompactComboboxRow<ChannelLayout>
						label="Tipo de sala"
						description="Escolha como esta sala aparece e funciona para os participantes."
						value={field.value}
						onChange={field.onChange}
						options={layoutOptions}
						isSearchable={false}
						isClearable={false}
						controlWidth="medium"
						dataFlx="channel.channel-tabs.channel-overview-tab.channel-layout"
					/>
				)}
			/>
			<Controller
				name="title_color"
				control={form.control}
				render={({field}) => (
					<ColorPickerField
						label={i18n._(TITLE_COLOR_DESCRIPTOR)}
						description={i18n._(TITLE_COLOR_DESCRIPTION_DESCRIPTOR)}
						value={field.value === 0 ? 0xffd400 : field.value}
						onChange={(value) => field.onChange(value >>> 0)}
					/>
				)}
			/>
			<Controller
				name="title_animation"
				control={form.control}
				render={({field}) => (
					<CompactComboboxRow<ChannelTitleAnimation>
						label={i18n._(TITLE_ANIMATION_DESCRIPTOR)}
						description={i18n._(TITLE_ANIMATION_DESCRIPTION_DESCRIPTOR)}
						value={field.value}
						onChange={field.onChange}
						options={animationOptions}
						isSearchable={false}
						isClearable={false}
						controlWidth="medium"
						dataFlx="channel.channel-tabs.channel-overview-tab.channel-title-animation"
					/>
				)}
			/>
		</>
	);
};
